import { PROTECTION_TIERS, PROTECTION_EXCLUSIONS } from "./protectionTiers";
import { AUTO_SERVICE_HOURS, autoStages, AUTO_STAGE_NAMES } from "./autoCatalogue";

// scopeTemplates.js — client-facing scope templates, shared by the
// calculators (auto-filling the scope box) and every quote renderer (used
// only when a quote's scope is empty - see resolveClientScope).

// Edit these to customise client-facing scope language per category/application.
// Add new entries following the same pattern: "Category > Application": "text"
export const SCOPE_TEMPLATES = {
  // Industrial scopes: a one-line summary then "• " bullets (rendered as a
  // list on the quote and job sheet). Plain descriptions only - no claims of
  // meeting standards or guaranteed outcomes.

  // ── INDUSTRIAL ──────────────────────────────────────────────────────────────────
  "industrial > Machinery Cleaning":
    "Dry ice clean of the agreed machinery, in place.\n" +
    "• Designated machinery surfaces cleaned of grease and process build-up\n" +
    "• No disassembly needed\n" +
    "• No water used and no blasting media left behind",

  "industrial > Production Line Cleaning":
    "Dry ice clean of the production line and associated equipment.\n" +
    "• Line components, conveyors and associated equipment\n" +
    "• Contamination and process build-up removed\n" +
    "• No water or chemicals used, so the line can usually restart soon after",

  "industrial > Electrical Panel Cleaning":
    "Dry ice clean of electrical panels and control cabinets.\n" +
    "• Panels, control cabinets and associated switchgear\n" +
    "• Dust, carbon tracking and surface contamination removed\n" +
    "• No water or chemicals used; dry ice does not conduct electricity\n" +
    "• Live or isolated working agreed with you before the job",

  "industrial > Grease & Oil Removal":
    "Dry ice removal of heavy grease and oil.\n" +
    "• Machinery, structures and floor surfaces as agreed\n" +
    "• Heavy grease, oil and hydrocarbon build-up lifted\n" +
    "• No solvents or water used, so there is little waste beyond the removed grease itself",

  "industrial > Fire / Damage Restoration":
    "Dry ice clean of fire- and smoke-damaged areas.\n" +
    "• Affected structures, machinery and surfaces as agreed\n" +
    "• Soot, char and smoke residue removed\n" +
    "• No water used, so nothing is soaked in the process",

  // ── MARINE ──────────────────────────────────────────────────────────────────────
  "marine > Engine Bay Cleaning":
    "Dry ice clean of the engine bay and bilge areas.\n" +
    "• Engine bay surfaces, bilge areas and associated components\n" +
    "• Oil, salt deposits and carbon removed\n" +
    "• No water used and no blasting media left behind in enclosed spaces",

  "marine > Salt Removal":
    "Dry ice removal of salt and marine deposits.\n" +
    "• Hull surfaces, deck fittings and structural steelwork as agreed\n" +
    "• Salt crystallisation, marine growth and loose corrosion removed\n" +
    "• Non-abrasive, so the surface underneath isn't worn away",

  "marine > Restoration Prep":
    "Dry ice surface preparation ahead of refinishing or repair.\n" +
    "• Existing coatings, fouling and contamination removed as agreed\n" +
    "• Surfaces left clean and dry, ready for the next stage\n" +
    "• Refinishing, antifouling and repairs are quoted separately",

  // ── AVIATION ────────────────────────────────────────────────────────────────────
  "aviation > Engine Component Cleaning":
    "Dry ice clean of engine components.\n" +
    "• Engine components, turbine housings and associated assemblies\n" +
    "• Carbon deposits, grease and burnt residues removed\n" +
    "• Non-abrasive, with no blasting media left behind",

  "aviation > Airframe Surface Cleaning":
    "Dry ice clean of airframe surfaces.\n" +
    "• Airframe surfaces, landing gear assemblies and structural components as agreed\n" +
    "• Contamination, overspray and surface deposits removed\n" +
    "• Non-abrasive; suitable for composite, aluminium and painted surfaces",

  "aviation > Interior Cleaning":
    "Dry ice clean of interior components.\n" +
    "• Interior components, avionics bays and cabin structures as agreed\n" +
    "• Contamination, odour-causing residue and biological residues removed\n" +
    "• No water or chemicals used near sensitive electronics",

  // ── AGRICULTURE ─────────────────────────────────────────────────────────────────
  "agriculture > Equipment Cleaning":
    "Dry ice clean of agricultural machinery and equipment.\n" +
    "• Machinery, implements and storage equipment as agreed\n" +
    "• Soil, crop residue, grease and biological contamination removed\n" +
    "• No water or chemicals used",

  "agriculture > Storage Cleaning":
    "Dry ice clean of storage buildings and structures.\n" +
    "• Grain and crop stores and associated structures as agreed\n" +
    "• Mould, pest residue and surface deposits removed\n" +
    "• No water used, so stores aren't left damp",

  "agriculture > Pest Residue Removal":
    "Dry ice removal of pest residue.\n" +
    "• Agricultural structures and machinery as agreed\n" +
    "• Nesting material, droppings and associated residue removed\n" +
    "• No chemical agents used",

  // ── DATA CENTRES ────────────────────────────────────────────────────────────────
  "data_centres > Live Equipment Cleaning":
    "Dry ice clean of data centre equipment.\n" +
    "• Equipment, server racks and infrastructure as agreed\n" +
    "• Dust and surface contamination removed\n" +
    "• No water used; dry ice does not conduct electricity\n" +
    "• Live working agreed with you before the job",

  "data_centres > Server Rack Cleaning":
    "Dry ice clean of server racks and associated hardware.\n" +
    "• Server racks, UPS systems and associated hardware\n" +
    "• Dust and surface contamination removed, clearing airflow paths\n" +
    "• Whether equipment stays powered is agreed with you before the job",

  "data_centres > Raised Floor Cleaning":
    "Dry ice clean of raised floor voids and cable trays.\n" +
    "• Floor voids, cable trays and sub-floor structures\n" +
    "• Accumulated dust and debris removed\n" +
    "• No water used near underfloor cabling",

  "data_centres > Cable Infrastructure Cleaning":
    "Dry ice clean of cable management systems.\n" +
    "• Cable trays, conduit runs and trunking as agreed\n" +
    "• Dust and surface deposits removed\n" +
    "• Non-abrasive, so cable insulation and connectors aren't worn",

  // ── FACILITIES & LEISURE ────────────────────────────────────────────────────────
  "facilities > Plant Room Cleaning":
    "Dry ice clean of plant room equipment.\n" +
    "• Pipework, pumps and associated mechanical services as agreed\n" +
    "• Contamination, scale and surface deposits removed\n" +
    "• No water or chemicals used",

  "facilities > Pool Plant Cleaning":
    "Dry ice clean of pool plant equipment.\n" +
    "• Filtration systems, pump housings and associated plant as agreed\n" +
    "• Scale, biofilm and chemical deposits removed\n" +
    "• No chemicals added; whether the pool needs isolating is agreed before the job",

  "facilities > Gym Equipment Cleaning":
    "Dry ice clean of gym equipment.\n" +
    "• Frames and mechanical components as agreed\n" +
    "• Grease, sweat residue and grime removed\n" +
    "• No water used near upholstery, electronics or painted finishes",

  // ── CATERING & HOSPITALITY ──────────────────────────────────────────────────────
  "catering > Kitchen Equipment Cleaning":
    "Dry ice clean of commercial kitchen equipment.\n" +
    "• Ovens, fryers and associated appliances as agreed\n" +
    "• Baked-on carbon, grease and food residue removed\n" +
    "• No chemicals used, so equipment can go back into use soon after",

  "catering > Extraction & Duct Cleaning":
    "Dry ice clean of kitchen extraction.\n" +
    "• Canopies, accessible ductwork and fan systems as agreed\n" +
    "• Grease build-up removed\n" +
    "• Duct access and any formal ductwork certification are agreed separately",

  "catering > Cold Store Cleaning":
    "Dry ice clean of cold store interiors.\n" +
    "• Interiors, evaporator units and racking as agreed\n" +
    "• Mould and food residue removed\n" +
    "• No water used, so no extra condensation",

  // ── HEALTHCARE ──────────────────────────────────────────────────────────────────
  "healthcare > Theatre Equipment Cleaning":
    "Dry ice clean of theatre equipment.\n" +
    "• Operating table assemblies and associated equipment as agreed\n" +
    "• Contamination and residues removed\n" +
    "• No chemicals or water used; your own infection-control and sterilisation steps still apply",

  "healthcare > Medical Equipment Cleaning":
    "Dry ice clean of medical equipment.\n" +
    "• Imaging systems and associated devices as agreed\n" +
    "• Contamination, residues and surface deposits removed\n" +
    "• Non-abrasive and dry, for use around sensitive electronics",

  "healthcare > Sterile Area Prep":
    "Dry ice clean ahead of sterile area preparation.\n" +
    "• Sterile areas, clean rooms and controlled environments as agreed\n" +
    "• Contamination and surface deposits removed\n" +
    "• No water used; final disinfection and requalification remain with your own team",

};

// ─── AUTOMOTIVE SCOPE TEMPLATES ──────────────────────────────────────────────
export const AUTO_SCOPE_TEMPLATES = {
  // Each is a one-line summary then "• " bullets (rendered as a list on the
  // quote and job sheet); buildAutoScopeBody puts the service name above it.

  fullUnderbody:
    "A complete dry ice clean of everything underneath, with the wheels off.\n" +
    "• Vehicle lifted and sensitive components masked\n" +
    "• Undertrays removed where fitted; wheels removed, protected and racked\n" +
    "• Dry ice cleaned: floorpan and chassis, subframes, suspension and steering, brakes and hubs, drivetrain and gearbox, exhaust and heat shields, and all four wheel arches (liners removed where accessible)\n" +
    "• Road dirt, salt, grease and loose or flaking coatings removed\n" +
    "• Wheel backs, inner barrels and brake calipers cleaned, including brake dust\n" +
    "• Everything refitted, with wheel nuts torqued to the manufacturer's specification",

  underbodyOnly:
    "Dry ice CO₂ clean of the full underfloor pan.\n" +
    "• Floorpan and sills, front to rear\n" +
    "• Road contamination, salt crystallisation and surface soiling removed",

  wheelArches:
    "Dry ice CO₂ clean of all four wheel arches.\n" +
    "• Arch liners removed where accessible, cleaned separately and refitted\n" +
    "• Arch lips, inner wings and structural faces\n" +
    "• Compacted mud, salt deposits and road film removed",

  suspensionBrakes:
    "Dry ice CO₂ clean of the suspension and braking assemblies, without disassembly.\n" +
    "• Subframes, wishbones and damper housings\n" +
    "• Brake calipers and associated hardware\n" +
    "• Road contamination, brake dust, grease and loose surface corrosion removed",

  engineBay:
    "A dry ice clean of the whole engine bay.\n" +
    "• Engine, ancillaries, bulkhead, inner wings and wiring looms\n" +
    "• Removes oil, carbon deposits and built-up grime\n" +
    "• Non-conductive and safe around electrics and sensors",

  drivetrain:
    "Dry ice CO₂ clean of the gearbox and driveline, without disassembly.\n" +
    "• Gearbox casing and bellhousing\n" +
    "• Driveline components\n" +
    "• Heavy grease, road contamination and loose surface corrosion removed",

  exhaust:
    "Dry ice CO₂ clean of the exhaust system.\n" +
    "• Manifolds and downpipes\n" +
    "• Catalytic converters and silencer boxes\n" +
    "• Heat shields\n" +
    "• Carbon bake, heat-bonded deposits and loose surface corrosion reduced",

  fullExteriorDetail:
    "Dry ice CO₂ preparation of the exterior, including areas conventional detailing can't reach.\n" +
    "• Panel faces, door jambs and shut lines\n" +
    "• Sill bases, and bonnet and boot apertures\n" +
    "• Road film, tar, polish residue and soiling removed\n" +
    "Not included: paint correction, polishing and coatings, which our detailing partners can quote as a combined package.",

  concoursInterior:
    "Dry ice CO₂ clean of the interior's structural and semi-concealed areas.\n" +
    "• Sill channels, door aperture faces and pillar bases\n" +
    "• Carpet tacking strips and seat runner channels\n" +
    "• Under-dash structures",

  underseelRemoval:
    "Dry ice CO₂ removal of applied underseal, bitumen sound-deadening and aged underbody coatings.\n" +
    "• Coating fractured and lifted by thermal shock, without grinding or abrasive blasting\n" +
    "• Original metalwork, factory seam welds and chassis stampings left intact\n" +
    "• Bare metal exposed for accurate corrosion inspection",

};

// The Automotive scope for a quote's state (used when a quote has no saved
// scope): the numbered sections, built the same way the calculator builds them.
export function buildAutoScope(state) {
  const body = buildAutoScopeBody(state.services, state.addons, state.protection);
  return body ? composeAutoScope({ body, state }) : "";
}

// The scope a quote prints: the saved client scope exactly as written when
// it has any text - never with templates added - and the matching templates
// only when it's empty. Industrial: template for category > application;
// Automotive: the ticked services' templates.
export function resolveClientScope(state) {
  const saved = (state?.notes?.clientScope || "").trim();
  if (saved) return state.notes.clientScope;
  if (Array.isArray(state?.services)) return buildAutoScope(state);
  const svc = state?.service;
  return (svc && SCOPE_TEMPLATES[`${svc.category} > ${svc.subService}`]) || "";
}

// ─── Scope of Works builder (calculators) ───────────────────────────────────
// The scope is three parts, saved together as notes.clientScope (what every
// quote prints): the template text for the selection (read-only until
// "Edit template text" unlocks it) and the estimator's own "Additional scope
// notes". Automotive arranges them in numbered sections (composeAutoScope).
// Notes fields: scopeV 2, scopeCustom + scopeCustomText (the unlocked,
// edited template text), scopeExtra, scopeLegacy (an older saved scope kept
// word for word: nothing appended to it automatically).

// Scope heading per ticked service: its calculator name, shortened where
// that name carries a note in brackets (headings can't contain brackets).
const AUTO_SCOPE_HEADINGS = { wheelArches: "Wheel Arches" };
const autoScopeHeading = k => AUTO_SCOPE_HEADINGS[k] || AUTO_SERVICE_HOURS[k]?.label || "Other work";

// Protection systems that treat minor surface rust as part of the system, so
// Full Underbody only excludes rust treatment beyond that. Dinitrol's full
// system does; Lanoguard, Dynax-UB and IGL Aegis go over the cleaned surface
// with no rust treatment step, so they keep the plain exclusion.
const PROTECTION_TREATS_SURFACE_RUST = ["dinitrol"];

// Full Underbody's "Not included" line, built from what's NOT on the quote:
// full underseal/bitumen removal drops out when that service is ticked, rust
// treatment when Rust Stabilisation is (and narrows to "beyond minor surface
// rust" under a system that treats it); repairs found are always excluded.
export function autoExclusionsLine(services, addons, protection) {
  const out = [
    !(services || []).includes("underseelRemoval") && "full removal of sound factory underseal or bitumen (available as an extra)",
    !(addons || []).includes("rustStabilisation") && (PROTECTION_TREATS_SURFACE_RUST.includes(protection) ? "rust treatment beyond minor surface rust" : "rust treatment"),
    "any repairs found",
  ].filter(Boolean);
  if (out.length === 1) return `Not included: ${out[0]}.`;
  // A comma before "and" when the item before it ends in brackets, so the
  // bracket doesn't run into the last item.
  const head = out.slice(0, -1).join(", ");
  return `Not included: ${head}${head.endsWith(")") ? "," : ""} and ${out[out.length - 1]}.`;
}

// Template text for the ticked services, each under its own "Service Name:"
// heading, in calculator order. addons and protection: the ticked add-ons and
// protection system, for Full Underbody's exclusions line.
export function buildAutoScopeBody(services, addons, protection) {
  const picked = new Set(services || []);
  const order = [...Object.keys(AUTO_SERVICE_HOURS), ...(services || []).filter(k => !AUTO_SERVICE_HOURS[k])];
  return order.filter(k => picked.has(k) && AUTO_SCOPE_TEMPLATES[k])
    .map(k => `${autoScopeHeading(k)}:\n${AUTO_SCOPE_TEMPLATES[k]}${k === "fullUnderbody" ? "\n" + autoExclusionsLine(services, addons, protection) : ""}`)
    .join("\n\n");
}

// Words that name each system in the estimator's own scope notes.
const PROTECTION_SCOPE_MATCH = {
  lanoguard: ["lanoguard"],
  biltHamber: ["bilt hamber", "dynax"],
  dinitrol: ["dinitrol"],
  ceramic: ["aegis", "igl"],
};
// Whether a piece of the estimator's notes names the selected protection system.
export function namesProtection(protection, text) {
  const t = String(text || "").toLowerCase();
  return (PROTECTION_SCOPE_MATCH[protection] || []).some(w => new RegExp(`\\b${w}\\b`).test(t));
}

// The Automotive scope as numbered sections in job order, with the same
// names as the quote's At a glance box (AUTO_STAGE_NAMES):
//   1. Clean: the service templates (bullets and the Not included line)
//   2. Rust treatment: one line, if Rust Stabilisation is ticked
//   3. Protection: the system, any notes that name it, then its guarantee
//      and exclusions (the quote has no separate guarantee section)
//   4. Additional work: custom labour not marked internal
//   5. Add-ons: anything else ticked, and collection and delivery
// Numbers run on from whatever's present. Additional scope notes that don't
// name the system print last. Saved as notes.clientScope, so a quote prints
// its scope exactly as it was saved.
export function composeAutoScope({ body, state, extra }) {
  const st = autoStages(state);
  const N = AUTO_STAGE_NAMES;
  const tier = st.protection ? PROTECTION_TIERS[state.protection] : null;
  const paras = String(extra || "").split(/\n\s*\n/).map(x => x.trim()).filter(Boolean);
  const protNotes = tier ? paras.filter(x => namesProtection(state.protection, x)) : [];
  const otherNotes = paras.filter(x => !protNotes.includes(x));
  const sections = [
    body && [N.clean, body.trim()],
    st.rust && [N.rust, `Exposed corrosion treated with ${st.rust.product ? `${st.rust.product} rust converter` : "a rust converter"}, allowed to dry fully, before protection.`],
    tier && [N.protection, [tier.label, ...protNotes, tier.longevity, tier.guarantee, PROTECTION_EXCLUSIONS].filter(Boolean).join("\n")],
    st.additional.length && [N.additional, st.additional.map(l => `• ${l}`).join("\n")],
    st.addOns.length && [N.addOns, st.addOns.map(l => `• ${l}`).join("\n")],
  ].filter(Boolean);
  return [...sections.map(([name, text], i) => `${i + 1}. ${name}:\n${text}`), ...otherNotes].join("\n\n");
}

// A saved Automotive scope written in numbered sections (composeAutoScope),
// as opposed to an older free-text one.
export const isSectionedScope = text => /^\d+\. Clean:$/m.test(String(text || ""));
// Whether a sectioned scope already carries the protection guarantee.
export const scopeHasProtection = text => /^\d+\. Protection:$/m.test(String(text || ""));

// The scope text a quote saves and prints (Industrial, and older Automotive
// scopes kept word for word): the template text, then the notes.
export function composeScope({ body, extra }) {
  return [body, extra].map(x => (x || "").trim()).filter(Boolean).join("\n\n");
}

// Notes as the calculator works with them. A quote saved before this
// builder keeps its scope exactly as saved (scopeLegacy, shown as edited
// template text, nothing added); one with no saved scope starts on the
// template.
export function upgradeScopeNotes(n) {
  if (!n || n.scopeV === 2) return n;
  const saved = n.clientScope || "";
  return saved.trim()
    ? { ...n, scopeV: 2, scopeCustom: true, scopeCustomText: saved, scopeExtra: "", scopeLegacy: true }
    : { ...n, scopeV: 2, scopeCustom: false, scopeCustomText: "", scopeExtra: "", scopeLegacy: false };
}

const escHTML = s => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
// Scope text for the quote documents and job sheet: escaped, line breaks
// kept, a short line ending in ":" on its own (e.g. "Engine Bay:") shown as a
// bold heading, and runs of "• " or "- " lines as a bulleted list.
export function scopeTextToHTML(text) {
  let out = "", inList = false, afterText = false;
  for (const line of String(text || "").split("\n")) {
    const bullet = line.trim().match(/^[•-]\s+(.*)$/);
    if (bullet) {
      if (!inList) { out += `<ul style="margin:4px 0 6px;padding-left:20px;">`; inList = true; }
      out += `<li style="margin:2px 0;">${escHTML(bullet[1])}</li>`;
      afterText = false;
      continue;
    }
    if (inList) { out += "</ul>"; inList = false; }
    // "1. Clean:" - a numbered section heading (composeAutoScope), on its own line.
    const sec = line.trim().match(/^(\d+\. [A-Z][A-Za-z &/,'-]{1,40}):$/);
    if (sec) { out = out.replace(/(<br\/>)+$/, ""); out += `<strong class="scope-sec" style="display:block;margin-top:12px;font-size:1.08em;color:#0e1520;">${escHTML(sec[1])}</strong>`; afterText = false; continue; }
    if (afterText) out += "<br/>";
    out += /^[A-Z][A-Za-z &/,'-]{1,40}:$/.test(line.trim())
      ? `<strong style="display:inline-block;margin-top:6px;">${escHTML(line.trim().slice(0, -1))}</strong>`
      : escHTML(line);
    afterText = true;
  }
  if (inList) out += "</ul>";
  return out;
}

